'use client';

import React, { useState, useRef } from 'react';
import styles from './styles/modals.module.css';
import { COURSE_FIELD_RULES } from '@/config/courseFieldRules';

const {
  totalPrice: TOTAL_PRICE_RULES,
  courseDuration: COURSE_DURATION_RULES,
  ageMin: AGE_MIN_RULES,
  ageMax: AGE_MAX_RULES,
  minStudents: MIN_STUDENTS_RULES,
  maxStudents: MAX_STUDENTS_RULES,
} = COURSE_FIELD_RULES;

export default function SidebarModal({
  isOpen,
  onClose,
  sidebarContent,
  setSidebarContent,
  onSave,
}) {
  // Add null safety checks for sidebarContent and provide default values
  const safeContent = sidebarContent || {};

  // States for all the titles and descriptions with null safety
  const [totalPrice, setTotalPrice] = useState(safeContent.totalPrice || 0);
  const [singleClassDurationNumber, setSingleClassDurationNumber] = useState(
    safeContent.singleClassDuration?.split(' ')?.[0] || ''
  );
  const [singleClassDurationUnit, setSingleClassDurationUnit] = useState(
    safeContent.singleClassDuration?.split(' ')?.[1] || ''
  );
  const [singleClassDuration, setSingleClassDuration] = useState(
    safeContent.singleClassDuration || ''
  );
  const [courseDuration, setCourseDuration] = useState(safeContent.courseDuration || 1);
  const [numLearnersMin, setNumLearnersMin] = useState(
    safeContent.numLearners?.split('-')?.[0] || ''
  );
  const [numLearnersMax, setNumLearnersMax] = useState(
    safeContent.numLearners?.split('-')?.[1] || ''
  );
  const [ageMin, setAgeMin] = useState(safeContent.ages?.split('-')?.[0] || '');
  const [ageMax, setAgeMax] = useState(safeContent.ages?.split('-')?.[1] || '');

  // use ref from html form for extra errors
  const ref = useRef(null);
  if (!isOpen) return null;
  const handleSubmit = (e) => {
    e.preventDefault();

    //check html form errors
    if (!ref.current.checkValidity()) {
      ref.current.reportValidity();
      return;
    }

    // set the autofillable data fields etc
    const weeklyPrice = Number((totalPrice / courseDuration).toFixed(2));
    const singleClassDuration = `${singleClassDurationNumber} ${singleClassDurationUnit}`;
    const numLearners = `${numLearnersMin}-${numLearnersMax}`;
    const ages = `${ageMin}-${ageMax}`;

    onSave(totalPrice, weeklyPrice, singleClassDuration, courseDuration, numLearners, ages);
  };

  return (
    <div className={styles.modal} onClick={onClose}>
      <div
        className={styles.doubleColumnModalContent}
        onClick={(e) => e.stopPropagation()}
      >
        <span className={styles.close} onClick={onClose}>
          &times;
        </span>
        <h2>Edit Sidebar:</h2>

        <form ref={ref} onSubmit={handleSubmit}>
          <div className={styles.entriesContainer} id="entries">
            <div className={styles.formColumn}>
              <label className={styles.formLabel} htmlFor="totalPrice">
                What is the price of the course (in USD)?
              </label>
              <input
                type="number"
                id="totalPrice"
                className={styles.formInput}
                value={totalPrice}
                onChange={(e) => setTotalPrice(Number(e.target.value))}
                min={TOTAL_PRICE_RULES.min}
                max={TOTAL_PRICE_RULES.max}
                step=".01"
                required={TOTAL_PRICE_RULES.required}
              />

              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  Please give the expected duration per class session.
                </h3>
                <div className={styles.inputGroup}>
                  <div>
                    <label
                      htmlFor="singleClassDurationNumber"
                      className={styles.formLabel}
                    >
                      Duration
                    </label>
                    <input
                      type="number"
                      id="singleClassDurationNumber"
                      value={singleClassDurationNumber}
                      onChange={(e) => setSingleClassDurationNumber(Number(e.target.value))}
                      min="1"
                      step="1"
                      className={styles.numberInput}
                      required
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="singleClassDurationUnit"
                      className={styles.formLabel}
                    >
                      Unit
                    </label>
                    <select
                      id="singleClassDurationUnit"
                      value={singleClassDurationUnit}
                      onChange={(e) => setSingleClassDurationUnit(e.target.value)}
                      className={styles.selectInput}
                      required
                    >
                      <option value="" disabled>
                        Select unit
                      </option>
                      <option value="minutes">Minute(s)</option>
                      <option value="hours">Hour(s)</option>
                    </select>
                  </div>
                </div>
              </div>

              <label className={styles.formLabel} htmlFor="courseDuration">
                What is the duration of this course (in weeks)?
              </label>
              <input
                type="number"
                id="courseDuration"
                className={styles.formInput}
                value={courseDuration}
                onChange={(e) => setCourseDuration(Number(e.target.value))}
                min={COURSE_DURATION_RULES.min}
                step="1"
                required={COURSE_DURATION_RULES.required}
              />
            </div>

            <div className={styles.formColumn}>
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  What is the recommended age range for this course?
                </h3>
                <div className={styles.inputGroup}>
                  <div>
                    <label htmlFor="ageMin" className={styles.formLabel}>
                      Min Age
                    </label>
                    <input
                      type="number"
                      id="ageMin"
                      value={ageMin}
                      onChange={(e) => setAgeMin(Number(e.target.value))}
                      min={AGE_MIN_RULES.min}
                      step="1"
                      className={styles.numberInput}
                      required={AGE_MIN_RULES.required}
                    />
                  </div>
                  <div>
                    <label htmlFor="ageMax" className={styles.formLabel}>
                      Max Age
                    </label>
                    <input
                      type="number"
                      id="ageMax"
                      value={ageMax}
                      onChange={(e) => setAgeMax(Number(e.target.value))}
                      min={Math.max(ageMin || 0, AGE_MAX_RULES.min)}
                      max={AGE_MAX_RULES.max}
                      step="1"
                      className={styles.numberInput}
                      required={AGE_MAX_RULES.required}
                    />
                  </div>
                </div>
              </div>

              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  What is the expected number of students in this course?
                </h3>
                <div className={styles.inputGroup}>
                  <div>
                    <label htmlFor="numLearnersMin" className={styles.formLabel}>
                      Min Learners
                    </label>
                    <input
                      type="number"
                      id="numLearnersMin"
                      value={numLearnersMin}
                      onChange={(e) => setNumLearnersMin(Number(e.target.value))}
                      min={MIN_STUDENTS_RULES.min}
                      max={MIN_STUDENTS_RULES.max}
                      step="1"
                      className={styles.numberInput}
                      required={MIN_STUDENTS_RULES.required}
                    />
                  </div>
                  <div>
                    <label htmlFor="numLearnersMax" className={styles.formLabel}>
                      Max Learners
                    </label>
                    <input
                      type="number"
                      id="numLearnersMax"
                      value={numLearnersMax}
                      onChange={(e) => setNumLearnersMax(Number(e.target.value))}
                      min={Math.max(
                        numLearnersMin || MIN_STUDENTS_RULES.min,
                        MIN_STUDENTS_RULES.min
                      )}
                      max={MAX_STUDENTS_RULES.max}
                      step="1"
                      className={styles.numberInput}
                      required={MAX_STUDENTS_RULES.required}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <button
            type="submit"
            className={`${styles.btn} ${styles.btnPrimary}`}
            style={{ marginTop: '1rem' }}
          >
            Save
          </button>
        </form>
      </div>
    </div>
  );
}